/* oxlint-disable no-inner-declarations */
import * as rq from '@tanstack/react-query';
import type { PickDeep } from 'type-fest';

import { appConfig } from '@/config';
import { useAuth } from '@/hooks/use-auth';
import { useMe } from '@/hooks/use-me';

import { communicationV1MeetingMinutesDeleteUpdate } from '../../__generated__/oazapfts-client';
import type { MeetingMinutesCommunication } from '../../entities/communication';
import { GetAllCommunications } from '../queries/get-all-communications';
import { GetOneMeetingCommunication } from '../queries/get-one-meeting-communication';

export namespace DeleteMeetingMinutes {
  export type Input = PickDeep<
    MeetingMinutesCommunication,
    'id' | 'meeting.id'
  >;
  export type Output = void;

  export function useMutation() {
    const queryClient = rq.useQueryClient();
    const auth = useAuth();
    const me = useMe();

    return rq.useMutation<Output, unknown, Input>({
      async mutationFn(input) {
        const accessToken = await auth.getAccessToken();
        const result = await communicationV1MeetingMinutesDeleteUpdate(
          {
            uuid: input.id,
            xUserName: me.displayName,
            xUserEmail: me.email,
          },
          {
            baseUrl: `${appConfig.bffUrl}/plex`,
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (result.status !== 204) {
          throw new Error('Fail to delete meeting minutes');
        }
      },
      async onSuccess(_, input) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: GetAllCommunications.key({}),
          }),
          input.meeting?.id
            ? queryClient.invalidateQueries({
                queryKey: GetOneMeetingCommunication.key({
                  id: input.meeting.id,
                }),
              })
            : undefined,
        ]);
      },
    });
  }
}
